import React, { useEffect, useState } from "react";
import { collection, query, where, getDocs, deleteDoc, doc, runTransaction } from "firebase/firestore";
import { db } from "../../index";
import UserProgress from "../../SiumShas/userStatus/userProgress";

const LearnersListAher = ({ sium, mappingData, siumId, isDisplayMode = false }) => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [learningListSorting, setLearningListSorting] = useState("status");
    const [sortDirection, setSortDirection] = useState("asc"); // asc or desc
    const [searchQuery, setSearchQuery] = useState("");
    const [optDeleteUser, setOptDeleteUser] = useState(null);
    const [selectedEmails, setSelectedEmails] = useState([]);
    const [selectedEmailsUrl, setSelectedEmailsUrl] = useState('mailto:?bcc=');
    const [showSortFilter, setShowSortFilter] = useState(false);
    const [learnersFilter, setLearnersFilter] = useState("all"); // "all" or "notFinished"
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);


    // Get last modified in days
    const getLastModifiedInDays = (lastModified) => {
        if (!lastModified) return 0;
        const date = lastModified.seconds ? new Date(lastModified.seconds * 1000) : new Date(lastModified);
        const mSecInDay = 24 * 60 * 60 * 1000;
        return Math.floor((Date.now() - date.getTime()) / mSecInDay);
    };

    // Fetch users
    useEffect(() => {
        // Calculate recommended percent based on sium dates
        const getRecommendedPercent = () => {
            if (!sium.startSium || !sium.endSium) return 0;

            const startD = sium.startSium.toDate();
            const endD = sium.endSium.toDate();
            const now = new Date();

            // If sium hasn't started yet, return 0
            if (now < startD) return 0;

            // If sium has ended, return 100
            if (now >= endD) return 100;

            // Calculate total duration and elapsed time in milliseconds
            const totalDuration = endD - startD;
            const elapsedTime = now - startD;

            // Avoid division by zero
            if (totalDuration <= 0) return 0;

            const percent = Math.floor((elapsedTime / totalDuration) * 100);
            return Math.min(100, Math.max(0, percent));
        };

        const fetchUsers = async () => {
            setLoading(true);
            try {
                const q = query(collection(db, "usersAher"), where("siumId", "==", siumId));
                const querySnapshot = await getDocs(q);

                const usersList = [];
                const recommendedPercent = getRecommendedPercent();

                querySnapshot.forEach((doc) => {
                    const userData = doc.data();
                    const pieces = userData.pieces || {};
                    const piecesCount = Object.keys(pieces).length;
                    const learnedCount = Object.values(pieces).filter(learned => learned === true).length;
                    const learnedPercent = piecesCount > 0 ? Math.floor((learnedCount / piecesCount) * 100) : 0;

                    usersList.push({
                        id: doc.id,
                        name: userData.fullName,
                        phone: userData.phone,
                        email: userData.email,
                        nishmat: userData.nishmat,
                        pieces: pieces,
                        piecesCount: piecesCount,
                        learnedCount: learnedCount,
                        learnedPercent: learnedPercent,
                        recommendedPercent: recommendedPercent,
                        lastModified: getLastModifiedInDays(userData.lastModified)
                    });
                });

                setUsers(usersList);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setLoading(false);
            }
        };

        if (siumId) {
            fetchUsers();
        }
    }, [siumId, sium]);

    // Filter users by search query and completion filter
    const filteredUsers = users.filter(user => {
        // Filter by completion status
        if (learnersFilter === "notFinished" && user.learnedPercent >= 100) return false;
        if (learnersFilter === "finished" && user.learnedPercent < 100) return false;

        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            user.name?.toLowerCase().includes(query) ||
            user.phone?.toLowerCase().includes(query) ||
            user.email?.toLowerCase().includes(query)
        );
    });

    // Sort users
    const sortedUsers = [...filteredUsers].sort((a, b) => {
        let result = 0;
        if (learningListSorting === "status") {
            result = a.learnedPercent - b.learnedPercent;
        } else if (learningListSorting === "date") {
            result = a.lastModified - b.lastModified;
        } else if (learningListSorting === "name") {
            result = a.name.localeCompare(b.name, 'he');
        }
        return sortDirection === "desc" ? -result : result;
    });

    // Toggle sort direction
    const toggleSortDirection = () => {
        setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    };

    // Handle sort change
    const handleSortChange = (sortType) => {
        if (learningListSorting === sortType) {
            toggleSortDirection();
        } else {
            setLearningListSorting(sortType);
            setSortDirection("asc");
        }
    };

    // Delete user with transaction for safe concurrent updates
    const deleteUser = async () => {
        if (!optDeleteUser) return;

        try {
            const userPieces = Object.keys(optDeleteUser.pieces || {});
            const siumRef = doc(db, "siumimAher", siumId);

            // Use transaction to safely update siumAher
            await runTransaction(db, async (transaction) => {
                // Get the latest sium data
                const siumDoc = await transaction.get(siumRef);
                if (!siumDoc.exists()) {
                    throw new Error("Sium not found");
                }

                const siumData = siumDoc.data();

                // Update learning pieces - release the user's pieces
                if (userPieces.length > 0 && siumData.learningPieces) {
                    const updatedPieces = siumData.learningPieces.map(piece => {
                        if (userPieces.includes(piece.id)) {
                            return { ...piece, taken: false, learned: false };
                        }
                        return piece;
                    });

                    const takenCount = updatedPieces.filter(p => p.taken).length;

                    transaction.update(siumRef, {
                        learningPieces: updatedPieces,
                        takenPieces: takenCount,
                        lastAccessed: new Date().getTime()
                    });
                }
            });

            // Delete the user document (outside transaction since it's a different collection)
            await deleteDoc(doc(db, "usersAher", optDeleteUser.id));

            // Refresh the page
            window.location.reload(false);
        } catch (error) {
            console.error("Error deleting user:", error);
            alert("שגיאה במחיקת הלומד");
        }
    };

    // Handle email selection for sending emails to learners
    const handleEmailSelection = (userId, email, isChecked) => {
        let updatedEmails = [...selectedEmails];

        if (isChecked) {
            updatedEmails.push(email);
        } else {
            updatedEmails = updatedEmails.filter(e => e !== email);
        }

        const mailtoUrl = `mailto:?bcc=${updatedEmails.join(',')}`;

        if (mailtoUrl.length > 2000) {
            alert('הכתובת ארוכה מדי. אנא בחר פחות לומדים.');
            document.getElementById(`email-${userId}`).checked = false;
            return;
        }

        setSelectedEmails(updatedEmails);
        setSelectedEmailsUrl(mailtoUrl);
    };

    // Share user page
    const shareUserPage = (user) => {
        if (navigator.share) {
            navigator.share({
                title: `סיום ${sium.bookName} של "${sium.kehilaName}"`,
                text: 'מצב הלמידה של ' + user.name,
                url: '/userStatusAher/' + user.id,
            });
        }
    };

    // Get WhatsApp link
    const getWhatsAppLink = (user, withLink) => {
        const countryCode = sium?.phonePrefix || '972';
        let cleanPhone = user.phone.replace(/\D/g, '');
        if (cleanPhone.startsWith(countryCode)) {
            cleanPhone = cleanPhone.substring(countryCode.length);
        }
        if (cleanPhone.startsWith('0')) {
            cleanPhone = cleanPhone.substring(1);
        }
        const phone = countryCode + cleanPhone;

        let message = `שלום ${user.name}`;
        if (withLink) {
            message += `\n\nניתן לעדכן את מצב הלמידה שלך בקישור הבא:\nhttps://siumshas.co.il/userStatusAher/${user.id}`;
        }
        return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    };

    // Display user pieces
    const displayUserPieces = (user) => {
        if (!mappingData || !mappingData.treeData) {
            return <p>טוען נתוני מיפוי...</p>;
        }

        const pieceIds = Object.keys(user.pieces || {});
        if (pieceIds.length === 0) return <p>אין חלקים</p>;

        // Build a tree structure containing only the user's pieces and their parents
        // Also calculate stats (total, learned) for each parent node
        const buildUserTree = (nodes, userPieceIds, userPiecesStatus) => {
            if (!nodes || !Array.isArray(nodes)) return [];

            const result = [];

            for (const node of nodes) {
                if (!node) continue;

                const hasChildren = node.children && node.children.length > 0;

                if (hasChildren) {
                    // Check if any descendant is a user piece
                    const childTree = buildUserTree(node.children, userPieceIds, userPiecesStatus);
                    if (childTree.length > 0) {
                        // Calculate stats from children
                        let totalCount = 0;
                        let learnedCount = 0;

                        const countStats = (children) => {
                            children.forEach(child => {
                                if (child.isLeaf) {
                                    totalCount++;
                                    if (child.isLearned) learnedCount++;
                                } else if (child.children) {
                                    countStats(child.children);
                                }
                            });
                        };
                        countStats(childTree);

                        result.push({
                            ...node,
                            children: childTree,
                            isLeaf: false,
                            totalCount,
                            learnedCount
                        });
                    }
                } else {
                    // Leaf node - check if it's a user piece
                    if (node.id && userPieceIds.includes(node.id)) {
                        result.push({
                            ...node,
                            isLeaf: true,
                            isLearned: userPiecesStatus[node.id] === true
                        });
                    }
                }
            }

            return result;
        };

        const userTree = buildUserTree(mappingData.treeData, pieceIds, user.pieces);

        if (userTree.length === 0) {
            return <p>לא נמצאו חלקים תואמים במיפוי</p>;
        }

        // Type labels for displaying node types in Hebrew
        const typeLabels = {
            nose: "נושא",
            helek: "חלק",
            kereh: "כרך",
            siman: "סימן",
            saif: "סעיף",
            perek: "פרק",
            daf: "דף",
            mizmor: "מזמור",
            tora: "תורה",
            shaar: "שער",
            masehet: "מסכת",
            sefer: "ספר",
            halahot: "הלכות",
            seder: "סדר",
            parashat: "פרשת",
            mishna: "משנה",
            alia: "עליה"
        };

        // Render tree node recursively
        const TreeNodeComponent = ({ node, depth = 0 }) => {
            const [isExpanded, setIsExpanded] = React.useState(false);
            const hasChildren = node.children && node.children.length > 0;
            const isLeaf = node.isLeaf;

            const getNodeDisplayName = () => {
                let name = node.name || '';
                if (node.type === 'daf' && node.dafFrom && node.dafTo) {
                    name += ` (${node.dafFrom} - ${node.dafTo})`;
                }
                if (node.type === 'siman' && node.simanFrom && node.simanTo) {
                    name += ` (${node.simanFrom} - ${node.simanTo})`;
                }
                return name;
            };

            return (
                <div className={`user-piece-tree-node ${!isLeaf ? 'parent-node' : ''}`} style={{ marginRight: `${depth * 15}px` }}>
                    <div
                        className={`user-piece-tree-header ${isLeaf ? (node.isLearned ? 'learned' : 'not-learned') : 'parent'}`}
                        onClick={() => hasChildren && setIsExpanded(!isExpanded)}
                        style={{ cursor: hasChildren ? 'pointer' : 'default' }}
                    >
                        {isLeaf ? (
                            node.isLearned ? (
                                <span className="piece-status-icon learned">✓</span>
                            ) : (
                                <span className="piece-status-icon not-learned">○</span>
                            )
                        ) : (
                            <span className={`piece-parent-icon ${isExpanded ? 'expanded' : ''}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <polyline points="9 18 15 12 9 6"></polyline>
                                </svg>
                            </span>
                        )}
                        <span className="piece-type-badge">{typeLabels[node.type] || node.type}</span>
                        <span className={`piece-node-name ${!isLeaf ? 'parent-name' : ''}`}>{getNodeDisplayName()}</span>
                        {!isLeaf && (
                            <span className="piece-stats">
                                <span className="badge bg-primary">נלמדו {node.learnedCount}/{node.totalCount}</span>
                            </span>
                        )}
                    </div>
                    {hasChildren && isExpanded && (
                        <div className="user-piece-tree-children">
                            {node.children.map(child => (
                                <TreeNodeComponent key={child.id} node={child} depth={depth + 1} />
                            ))}
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className="user-pieces-tree">
                {userTree.map(node => (
                    <TreeNodeComponent key={node.id} node={node} depth={0} />
                ))}
            </div>
        );
    };

    // Get user actions - only show when not in display mode
    const getUserActions = (user) => {
        if (isDisplayMode) return null;

        return (
            <div className="margin-top-20 d-flex flex-wrap">
                <div className="dropdown-center sium-button-container">
                    <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        פעולות בדף הלומד
                    </button>
                    <ul className="dropdown-menu lomed-actions-list">
                        <li className="dropdown_item-1">
                            <a className="btn sium-status-button" href={"/userStatusAher/" + user.id}>מעבר לדף הלומד</a>
                        </li>
                        <li className="dropdown_item-2">
                            <a className="btn sium-status-button" href={"/editUserAher/" + user.id}>עריכה</a>
                        </li>
                        <li className="dropdown_item-3">
                            <button className="btn sium-status-button" data-bs-toggle="modal" data-bs-target="#deleteUserModalAher"
                                    onClick={() => setOptDeleteUser(user)}>מחיקה</button>
                        </li>
                    </ul>
                </div>
                <div className="dropdown-center sium-button-container">
                    <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                            data-bs-toggle="dropdown" aria-expanded="false">
                        שיתוף דף הלומד
                    </button>
                    <ul className="dropdown-menu lomed-actions-list">
                        <li className="dropdown_item-1">
                            <button className="btn sium-status-button" onClick={() => shareUserPage(user)}>שיתוף קישור</button>
                        </li>
                        {user.phone && (
                            <li className="dropdown_item-2">
                                <a className="btn sium-status-button" href={getWhatsAppLink(user, true)} target="_blank" rel="noopener noreferrer">שיתוף ב-WhatsApp</a>
                            </li>
                        )}
                        {user.email && isMobile && (
                            <li className="dropdown_item-3">
                                <a className="btn sium-status-button" href={`mailto:${user.email}?subject=${encodeURIComponent('דף הסטטוס שלך')}&body=${encodeURIComponent('שלום ' + user.name + ',\n\nניתן לעדכן את מצב הלמידה שלך בקישור הבא:\nhttps://siumshas.co.il/userStatusAher/' + user.id)}`}>שיתוף במייל</a>
                            </li>
                        )}
                    </ul>
                </div>
                {(user.phone || (isMobile && user.email)) && (
                    <div className="dropdown-center sium-button-container">
                        <button className="btn btn-secondary dropdown-toggle sium-status-button dark-button" type="button"
                                data-bs-toggle="dropdown" aria-expanded="false">
                            יצירת קשר עם הלומד
                        </button>
                        <ul className="dropdown-menu lomed-actions-list">
                            {user.phone && <li className="dropdown_item-1"><a className="btn sium-status-button" href={"tel:+972" + user.phone}>להתקשר ללומד</a></li>}
                            {user.phone && <li className="dropdown_item-2"><a className="btn sium-status-button" href={"sms:+972" + user.phone}>SMS</a></li>}
                            {user.phone && <li className="dropdown_item-3"><a className="btn sium-status-button" href={getWhatsAppLink(user, false)} target="_blank" rel="noopener noreferrer">WhatsApp</a></li>}
                            {user.email && isMobile && <li className="dropdown_item-4"><a className="btn sium-status-button" href={`mailto:${user.email}`}>Email</a></li>}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Share learners login page
    const shareLomdimLoginPage = () => {
        if (navigator.share) {
            navigator.share({
                title: `סיום ${sium.bookName} של "${sium.kehilaName}"`,
                text: `סיום ${sium.bookName} של "${sium.kehilaName}" - כניסה ללומדים`,
                url: '/lomdimLoginAher/' + siumId
            });
        }
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <div className="updateStatusSpinner" style={{ width: '40px', height: '40px', borderWidth: '4px', margin: '0 auto' }}></div>
                <p className="mt-2">טוען רשימת לומדים...</p>
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="text-center p-4">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                </svg>
                <p className="mt-3">אין לומדים רשומים עדיין</p>
                <p className="text-muted">הוסף לומדים חדשים באמצעות כפתור "הוסף לומד"</p>
            </div>
        );
    }

    return (
        <div className="learners-list-aher">

            {/* Send email button - only when not in display mode and users have emails */}
            {!isDisplayMode && users.some(user => user.email) && (
                <button
                    className="btn dark-button share-display-sium margin-top-10"
                    data-bs-toggle="modal"
                    data-bs-target="#sendEmailModalAher"
                >
                    שלח מייל לכולם
                </button>
            )}

            {/* Learners login link button - only when not in display mode */}
            {!isDisplayMode && (
                <button className="btn dark-button share-display-sium" onClick={shareLomdimLoginPage}>
                    לינק כניסה ללומדים
                </button>
            )}

            {/* Sort & Filter toggle button */}
            <button
                className="btn dark-button share-display-sium margin-bottom-10"
                onClick={() => setShowSortFilter(!showSortFilter)}
            >
                מיון וסינון
                <span style={{ marginRight: '6px', fontSize: '12px' }}>{showSortFilter ? '▲' : '▼'}</span>
            </button>

            {showSortFilter && (<div className="sort-filter-section">
            {/* Sorting options - same design as SiumMasehtotStatus */}
            <div className="btn-group sorting-container margin-bottom-15" role="group" aria-label="sort learning list buttons">
                <label className="sorting-label">מיין לפי:</label>
                <button
                    type="button"
                    className={`btn sort-button ${learningListSorting === "status" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleSortChange("status")}
                >
                    מצב למידה
                    {learningListSorting === "status" && (
                        <span className="sort-direction-icon">
                            {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    className={`btn sort-button ${learningListSorting === "date" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleSortChange("date")}
                >
                    תאריך עדכון
                    {learningListSorting === "date" && (
                        <span className="sort-direction-icon">
                            {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    className={`btn sort-button ${learningListSorting === "name" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleSortChange("name")}
                >
                    שם הלומד
                    {learningListSorting === "name" && (
                        <span className="sort-direction-icon">
                            {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                    )}
                </button>
            </div>

            {/* Filter options */}
            <div className="btn-group sorting-container margin-bottom-10" role="group" aria-label="filter learners">
                <label className="sorting-label">הצג:</label>
                <button
                    type="button"
                    className={`btn sort-button ${learnersFilter === "all" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setLearnersFilter("all")}
                >
                    הכל
                </button>
                <button
                    type="button"
                    className={`btn sort-button ${learnersFilter === "notFinished" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setLearnersFilter("notFinished")}
                >
                    לא סיימו
                </button>
                <button
                    type="button"
                    className={`btn sort-button ${learnersFilter === "finished" ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setLearnersFilter("finished")}
                >
                    סיימו
                </button>
            </div>

            {/* Search input */}
            <div className="learners-search-container margin-bottom-10">
                {!searchQuery && <span className="learners-search-icon">🔍</span>}
                <input
                    type="text"
                    className="form-control learners-search-input"
                    placeholder="חיפוש לומד לפי שם, טלפון, אימייל..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                    <button
                        type="button"
                        className="learners-search-clear"
                        onClick={() => setSearchQuery("")}
                    >
                        ✕
                    </button>
                )}
            </div>
            </div>)}

            {/* Users list */}
            <div className="accordion margin-top-20" id="learningUsersListAher">
                {sortedUsers.map((user, uIndex) => (
                    <div className="accordion-item" key={user.id}>
                        <h3 className="accordion-header">
                            <button
                                className="accordion-button collapsed learner-list-button"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#collapseLearningAher${uIndex}`}
                                aria-expanded="false"
                                aria-controls={`collapseLearningAher${uIndex}`}
                            >
                                <span className="learner-badges">
                                {learningListSorting === "status" && (
                                    <>
                                        <span className="badge bg-primary margin-right-10">{user.learnedCount}/{user.piecesCount} חלקים נלמדו</span>
                                        <span className="badge bg-info margin-right-10">{user.learnedPercent}%</span>
                                    </>
                                )}
                                {learningListSorting === "date" && (
                                    <>
                                        <span className="badge bg-primary margin-right-10">{user.learnedCount}/{user.piecesCount} חלקים נלמדו</span>
                                        <span className="badge bg-info margin-right-10">לפני {user.lastModified} ימים</span>
                                    </>
                                )}
                                {learningListSorting === "name" && (
                                    <>
                                        <span className="badge bg-primary margin-right-10">{user.learnedCount}/{user.piecesCount} חלקים נלמדו</span>
                                        <span className="badge bg-info margin-right-10">{user.learnedPercent}%</span>
                                    </>
                                )}
                                </span>
                                <span className="learner-name">{user.name}</span>
                            </button>
                        </h3>
                        <div
                            id={`collapseLearningAher${uIndex}`}
                            className="accordion-collapse collapse"
                            data-bs-parent="#learningUsersListAher"
                        >
                            <div className="accordion-body">
                                <UserProgress
                                    userPerc={user.learnedPercent}
                                    recommendation={user.recommendedPercent}
                                />

                                {getUserActions(user)}

                                {user.phone && (
                                    <div className="user-detail-modal check-user-modal mt-3">
                                        <svg fill="#000000" height="800px" width="800px" version="1.1" xmlns="http://www.w3.org/2000/svg"
                                             viewBox="0 0 473.806 473.806">
                                            <g>
                                                <g>
                                                    <path d="M374.456,293.506c-9.7-10.1-21.4-15.5-33.8-15.5c-12.3,0-24.1,5.3-34.2,15.4l-31.6,31.5c-2.6-1.4-5.2-2.7-7.7-4
                                                        c-3.6-1.8-7-3.5-9.9-5.3c-29.6-18.8-56.5-43.3-82.3-75c-12.5-15.8-20.9-29.1-27-42.6c8.2-7.5,15.8-15.3,23.2-22.8
                                                        c2.8-2.8,5.6-5.7,8.4-8.5c21-21,21-48.2,0-69.2l-27.3-27.3c-3.1-3.1-6.3-6.3-9.3-9.5c-6-6.2-12.3-12.6-18.8-18.6
                                                        c-9.7-9.6-21.3-14.7-33.5-14.7s-24,5.1-34,14.7c-0.1,0.1-0.1,0.1-0.2,0.2l-34,34.3c-12.8,12.8-20.1,28.4-21.7,46.5
                                                        c-2.4,29.2,6.2,56.4,12.8,74.2c16.2,43.7,40.4,84.2,76.5,127.6c43.8,52.3,96.5,93.6,156.7,122.7c23,10.9,53.7,23.8,88,26
                                                        c2.1,0.1,4.3,0.2,6.3,0.2c23.1,0,42.5-8.3,57.7-24.8c0.1-0.2,0.3-0.3,0.4-0.5c5.2-6.3,11.2-12,17.5-18.1c4.3-4.1,8.7-8.4,13-12.9
                                                        c9.9-10.3,15.1-22.3,15.1-34.6c0-12.4-5.3-24.3-15.4-34.3L374.456,293.506z"/>
                                                    <path d="M256.056,112.706c26.2,4.4,50,16.8,69,35.8s31.3,42.8,35.8,69c1.1,6.6,6.8,11.2,13.3,11.2c0.8,0,1.5-0.1,2.3-0.2
                                                        c7.4-1.2,12.3-8.2,11.1-15.6c-5.4-31.7-20.4-60.6-43.3-83.5s-51.8-37.9-83.5-43.3c-7.4-1.2-14.3,3.7-15.6,11
                                                        S248.656,111.506,256.056,112.706z"/>
                                                    <path d="M473.256,209.006c-8.9-52.2-33.5-99.7-71.3-137.5s-85.3-62.4-137.5-71.3c-7.3-1.3-14.2,3.7-15.5,11
                                                        c-1.2,7.4,3.7,14.3,11.1,15.6c46.6,7.9,89.1,30,122.9,63.7c33.8,33.8,55.8,76.3,63.7,122.9c1.1,6.6,6.8,11.2,13.3,11.2
                                                        c0.8,0,1.5-0.1,2.3-0.2C469.556,223.306,474.556,216.306,473.256,209.006z"/>
                                                </g>
                                            </g>
                                        </svg>
                                        {user.phone}
                                    </div>
                                )}

                                {user.email && (
                                    <div className="user-detail-modal mt-2 check-user-modal">
                                        <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
                                            <title>EMAIL</title>
                                            <path d="M123.25,24.192c0-.018,0-.034-.005-.052s-.007-.063-.009-.094a1.734,1.734,0,0,0-.083-.408c-.006-.018,0-.037-.011-.055s-.01-.015-.013-.023a1.734,1.734,0,0,0-.227-.407c-.021-.028-.043-.053-.066-.08a1.755,1.755,0,0,0-.31-.294c-.012-.009-.022-.02-.034-.028a1.744,1.744,0,0,0-.414-.2c-.034-.012-.068-.022-.1-.032a1.733,1.733,0,0,0-.474-.073H6.5a1.733,1.733,0,0,0-.474.073c-.035.01-.068.02-.1.032a1.744,1.744,0,0,0-.414.2c-.012.008-.022.019-.034.028a1.755,1.755,0,0,0-.31.294c-.022.027-.045.052-.066.08a1.734,1.734,0,0,0-.227.407c0,.008-.01.015-.013.023s-.005.037-.011.055a1.734,1.734,0,0,0-.083.408c0,.032-.009.063-.009.094s-.005.034-.005.052v79.615c0,.023.006.045.007.068a1.737,1.737,0,0,0,.019.188c.008.051.015.1.027.152a1.74,1.74,0,0,0,.056.179c.017.047.033.094.054.139a1.729,1.729,0,0,0,.093.172c.024.04.048.081.075.119a1.743,1.743,0,0,0,.125.152c.033.036.066.072.1.106.021.019.037.042.059.061s.036.017.052.03a1.736,1.736,0,0,0,.452.263c.035.014.071.022.107.033a1.732,1.732,0,0,0,.488.085c.012,0,.023.006.035.006l.023,0H121.478l.023,0c.012,0,.023-.006.034-.006a1.732,1.732,0,0,0,.489-.085c.035-.011.07-.019.1-.033a1.736,1.736,0,0,0,.453-.263c.016-.013.036-.017.052-.03s.038-.042.059-.061c.036-.034.069-.069.1-.106a1.743,1.743,0,0,0,.125-.152c.027-.038.051-.078.075-.119a1.729,1.729,0,0,0,.093-.172c.021-.045.037-.092.054-.139a1.74,1.74,0,0,0,.056-.179c.012-.05.019-.1.027-.152a1.737,1.737,0,0,0,.019-.188c0-.023.007-.045.007-.068ZM45.8,60.316,62.858,74.993a1.751,1.751,0,0,0,2.283,0L82.2,60.316l35.512,41.741H10.289ZM8.25,99.052V28.007l34.9,30.026Zm76.6-41.019,34.9-30.026V99.052Zm31.931-32.091L81.276,56.493c-.006.005-.014.008-.02.014s-.012.014-.019.02L64,71.358,46.763,56.527c-.007-.006-.012-.014-.019-.02l-.02-.014L11.217,25.942Z"/>
                                        </svg>
                                        {user.email}
                                    </div>
                                )}

                                {user.nishmat && (
                                    <div className="user-detail-modal mt-2 check-user-modal">
                                        <span>{user.nishmat}</span>
                                    </div>
                                )}

                                <div className="user-detail-modal mt-2 check-user-modal">
                                    <svg id="Layer_1" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
                                        <title>COUNTDOWN TIMER</title>
                                        <path d="M64,12.75a51.186,51.186,0,0,0-37.2,16l-1.059-7.918a1.25,1.25,0,0,0-2.479.332L24.68,31.773a1.251,1.251,0,0,0,1.239,1.084h9.807a1.25,1.25,0,0,0,0-2.5h-7a48.594,48.594,0,1,1-6.729,8.887,1.25,1.25,0,1,0-2.152-1.271A51.256,51.256,0,1,0,64,12.75Z"/><path d="M94.009,86.526a1.249,1.249,0,0,0,1.741-.306,38.764,38.764,0,1,0-7.2,7.759,1.25,1.25,0,0,0-1.586-1.934A36.218,36.218,0,1,1,93.7,84.785,1.25,1.25,0,0,0,94.009,86.526Z"/><path d="M62.75,38.356v24.4l-17.361.094a1.25,1.25,0,0,0,.007,2.5H45.4l18.6-.1A1.25,1.25,0,0,0,65.25,64V38.356a1.25,1.25,0,0,0-2.5,0Z"/>
                                    </svg>
                                    <span>לומד עדכן מצב למידה לפני {user.lastModified} ימים</span>
                                </div>

                                <div className="mt-3">
                                    <h5 className="user-pieces-header">החלקים של הלומד ({user.piecesCount})</h5>
                                    {displayUserPieces(user)}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Delete User Modal - only show when not in display mode */}
            {!isDisplayMode && (
            <div className="modal fade" id="deleteUserModalAher" tabIndex="-1" aria-labelledby="deleteUserModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">מחיקת לומד</h5>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {optDeleteUser && (
                                <p>האם אתה בטוח שברצונך למחוק את הלומד <strong>{optDeleteUser.name}</strong>?</p>
                            )}
                            <p className="text-muted">פעולה זו תשחרר את כל החלקים שהלומד לקח.</p>
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">ביטול</button>
                            <button type="button" className="btn btn-danger" data-bs-dismiss="modal" onClick={deleteUser}>מחק</button>
                        </div>
                    </div>
                </div>
            </div>
            )}

            {/* Send Email Modal - only show when not in display mode */}
            {!isDisplayMode && (
            <div className="modal fade" id="sendEmailModalAher" tabIndex="-1" aria-labelledby="send email to users" aria-hidden="true">
                <div className="modal-dialog modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h1 className="modal-title fs-5">שליחת מייל לכולם</h1>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-check-list">
                                {users
                                    .filter(user => user.email)
                                    .map(user => (
                                        <div className="form-check emails-list" key={user.id}>
                                            <input
                                                className="form-check-input margin-left-15"
                                                type="checkbox"
                                                id={`email-${user.id}`}
                                                onChange={(e) => handleEmailSelection(user.id, user.email, e.target.checked)}
                                            />
                                            <label className="form-check-label" htmlFor={`email-${user.id}`}>
                                                {user.name}
                                            </label>
                                        </div>
                                    ))}
                            </div>
                            {!isMobile && selectedEmails.length > 0 && (
                                <div className="mt-3">
                                    <label className="form-label">כתובות המייל (העתק והדבק בתוכנת המייל שלך):</label>
                                    <textarea
                                        className="form-control"
                                        rows="4"
                                        readOnly
                                        value={selectedEmails.join(', ')}
                                    />
                                </div>
                            )}
                        </div>
                        <div className="modal-footer modal-footer-spread">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => {
                                    setSelectedEmails([]);
                                    setSelectedEmailsUrl('mailto:?bcc=');
                                    document.querySelectorAll('.form-check-input[id^="email-"]').forEach(cb => cb.checked = false);
                                }}
                            >
                                איפוס
                            </button>
                            <div className="modal-footer-left">
                                <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">ביטול</button>
                                {isMobile ? (
                                    <a
                                        className="btn btn-primary"
                                        href={selectedEmailsUrl}
                                        onClick={(e) => {
                                            if (!selectedEmailsUrl || selectedEmailsUrl === 'mailto:?bcc=') {
                                                e.preventDefault();
                                                alert('אנא בחר לפחות לומד אחד');
                                            }
                                        }}
                                    >
                                        שלח
                                    </a>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => {
                                            if (!selectedEmails.length) {
                                                alert('אנא בחר לפחות לומד אחד');
                                                return;
                                            }
                                            navigator.clipboard.writeText(selectedEmails.join(', '))
                                                .then(() => alert('כתובות המייל הועתקו ללוח'))
                                                .catch(() => alert('שגיאה בהעתקה'));
                                        }}
                                    >
                                        העתק
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            )}
        </div>
    );
};

export default LearnersListAher;

